'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { sendGTMEvent } from '@/utils/analytics';

import { useToast } from '@/contexts/ToastContext';
import { useConfirmModal } from '@/contexts/ConfirmModalContext';
import { useGlobal } from '@/contexts/GlobalContext';
import { useDrawer } from '@/contexts/DrawerContext';
import ConfirmModalContent from '@/components/layouts/ConfirmModalContent';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import Textarea from '@/components/common/Textarea';
import EditorShortcuts from '@/components/common/EditorShortcuts';
import FormLabel from '@/components/common/FormLabel';
import FormErrorText from '@/components/common/FormErrorText';
import { PrivateAicon } from '@/components/common/Aicons';
import PolygonDivider from '@/components/common/PolygonDivider';
import FootballlogGoalsEdit from '@/components/footballlogs/FootballlogGoalsEdit';
import FootballlogGoalsEditLoading from '@/components/footballlogs/FootballlogGoalsEditLoading';
import ScoreCardSlider, { ScoreCardData } from '@/components/scoreboard/ScoreCardSlider';
import SelectTacticalboard from '@/components/tacticalboard/SelectTacticalboard';
import SelectStats from '@/components/stats/SelectStats';
import { CustomNoteCheckState } from '@/components/footballlogs/CustomNoteDropdown';
import TeamLabel from '@/components/teams/TeamLabel';
import { TeamModel } from '@/types/teams';
import { FootballlogCreateDataModel } from '@/types/footballlogs';
import { GoalModel } from '@/types/goals';
import { useCurrentGoalData } from '@/hooks/swr/goals/useCurrentGoalData';

const MAX_LENGTH_TITLE = 50;
const MAX_LENGTH_GOOD = 800;
const MAX_LENGTH_BAD = 800;
const MAX_LENGTH_NEXT = 800;
const MAX_LENGTH_TEAM_A = 50;
const MAX_LENGTH_TEAM_B = 50;
const MAX_LENGTH_MEMO = 800;

/**
 * props
 */
interface FootballlogFormProps {
  // propsGoal: GoalModel | undefined;
  propsDateString: string;
  propsTeamNoteTeamUUID: string;
  checkStates: CustomNoteCheckState;
  onFormSubmit: (formData: FootballlogCreateDataModel) => Promise<void>;
}

/**
 * yup - バリデーションを定義
 * NOTE: 下記のtransformによって、未入力（空白）をundefinedに変換することで項目自体を
 * 送信しないようにする
 * NOTE: number|null型とする場合は、.default(null)が必要。
 * なぜ：
 * フォーム入力 -> 値削除 -> サブミット -> プロパティ自体が削除された状態で送信される。
 * 逆に、フォーム未入力 -> サブミット -> {hoge: null} と送信される。（上記パターンもこちらの挙動をとりたい）
 */
const createSchema = (bypassMemosValidation: boolean) => {
  return yup.object().shape({
    team_uuid: yup
      .string()
      .nullable()
      .default(null)
      .transform((value: string, originalValue) => {
        return originalValue === undefined || originalValue === '' ? null : value;
      }),
    title: yup
      .string()
      .required('この項目は必須です。')
      .matches(/^(?!\s*$).+/, 'この項目は必須です。')
      .max(MAX_LENGTH_TITLE, `${MAX_LENGTH_TITLE}文字以内で入力してください。`),
    goal_id: yup
      .number()
      .nullable()
      .default(null)
      .transform((value: number, originalValue) => {
        return originalValue === undefined || originalValue === '' ? null : value;
      }),
    date: yup.string().required('この項目は必須です。'),
    stats: yup
      .number()
      .nullable()
      .default(null)
      .transform((value: string, originalValue) => {
        return originalValue === undefined || originalValue === '' ? null : value;
      }),
    gbn_log: yup.object().shape({
      good: yup
        .string()
        .nullable()
        .default(null)
        .max(MAX_LENGTH_GOOD, `GOODは${MAX_LENGTH_GOOD}文字以内で入力してください。`)
        .transform((value: string, originalValue) => {
          return originalValue === undefined || originalValue === '' ? null : value;
        }),
      bad: yup
        .string()
        .nullable()
        .default(null)
        .max(MAX_LENGTH_BAD, `BADは${MAX_LENGTH_BAD}文字以内で入力してください。`)
        .transform((value: string, originalValue) => {
          return originalValue === undefined || originalValue === '' ? null : value;
        }),
      next: yup
        .string()
        .nullable()
        .default(null)
        .max(MAX_LENGTH_NEXT, `NEXTは${MAX_LENGTH_NEXT}文字以内で入力してください。`)
        .transform((value: string, originalValue) => {
          return originalValue === undefined || originalValue === '' ? null : value;
        })
    }),
    scores: yup
      .array()
      .required()
      .of(
        yup.object({
          team1: yup
            .string()
            .nullable()
            .default(null)
            .max(MAX_LENGTH_TEAM_A, `${MAX_LENGTH_TEAM_A}文字以内で入力してください。`)
            .transform((value: string, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team2: yup
            .string()
            .nullable()
            .default(null)
            .max(MAX_LENGTH_TEAM_B, `${MAX_LENGTH_TEAM_B}文字以内で入力してください。`)
            .transform((value: string, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team1_1st_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team2_1st_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team1_2nd_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team2_2nd_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team1_extra_1st_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team2_extra_1st_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team1_extra_2nd_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team2_extra_2nd_half_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team1_pk_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            }),
          team2_pk_score: yup
            .number()
            .nullable()
            .default(null)
            .transform((value: number, originalValue) => {
              return originalValue === undefined || originalValue === '' ? null : value;
            })
        })
      ),
    // NOTE: bypassMemosValidation=True(Memoチェックが外れている状態)の場合、
    // バリデーションをスキップするようにする。バリデーションエラーのまま項目非表示にした場合、
    // エラーが残り続けてサブミットができないため。
    memos: bypassMemosValidation
      ? yup
          .array()
          .required()
          .of(
            yup.object({
              memo: yup.string().nullable().default(null) // バリデーションなし
            })
          )
      : yup
          .array()
          .required()
          .of(
            yup.object({
              memo: yup
                .string()
                .nullable()
                .default(null)
                .matches(/^(?!\s*$).+/, '文字を入力してください。')
                .max(MAX_LENGTH_MEMO, `${MAX_LENGTH_MEMO}文字以内で入力してください。`)
                .transform((value: string, originalValue) => {
                  return originalValue === undefined || originalValue === '' ? null : value;
                })
            })
          ),
    scene: yup.object().shape({
      tactical_board: yup.string().transform((value: string, originalValue) => {
        return originalValue === '' ? undefined : value;
      })
    })
  });
};

/**
 * サッカーノート新規登録フォーム
 * @param param0
 * @returns
 */
const FootballlogForm: React.FC<FootballlogFormProps> = ({
  checkStates,
  propsDateString,
  propsTeamNoteTeamUUID,
  onFormSubmit
}) => {
  /**
   * useRouter
   */
  const navigate = useNavigate();

  /**
   * React Context
   */
  const context = useGlobal();
  const { showErrorMessage } = useToast();
  const { closeDrawer } = useDrawer();
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();

  // 現在設定中の目標を取得
  const {
    data: currentGoalData,
    isLoading: isLoadingCurrentGoalData,
    error: isErrorCurrentGoalData
  } = useCurrentGoalData();
  // NOTE: エラーメッセージを一度だけ表示するためのフラグ
  const hasShownCurrentGoalErrorToast = useRef(false);

  /**
   * useState
   */
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [noteCustomCheckStates, setNoteCustomCheckStates] = useState<CustomNoteCheckState>({
    isCheckedScore: checkStates.isCheckedScore,
    isCheckedMemo: checkStates.isCheckedMemo
  });
  const [seletctedTeam, setSelectedTeam] = useState<TeamModel | undefined>(undefined);
  const [goal, setGoal] = useState<GoalModel | undefined>(undefined);
  const [scores, setScores] = useState<ScoreCardData[]>([
    // NOTE: スコアON時にデフォルトで１つ表示したいので初期値を設定（※新規登録時だけ）
    {
      team1: undefined,
      team2: undefined,
      team1FirstHalfScore: undefined,
      team2FirstHalfScore: undefined,
      team1SecondHalfScore: undefined,
      team2SecondHalfScore: undefined,
      team1ExtraFirstHalfScore: undefined,
      team2ExtraFirstHalfScore: undefined,
      team1ExtraSecondHalfScore: undefined,
      team2ExtraSecondHalfScore: undefined,
      team1PKScore: undefined,
      team2PKScore: undefined
    }
  ]);

  /**
   * useRef
   */
  const textareaGoodRef = useRef<HTMLTextAreaElement | null>(null);
  const textareaBadRef = useRef<HTMLTextAreaElement | null>(null);
  const textareaNextRef = useRef<HTMLTextAreaElement | null>(null);
  const textareaMemoRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  // スキーマ作成
  const schema = createSchema(!noteCustomCheckStates.isCheckedMemo);

  /**
   * React Hook Form - のuseFormを使用してフォームを作成
   */
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    setFocus
  } = useForm<FootballlogCreateDataModel>({
    resolver: yupResolver(schema),
    defaultValues: {
      // team_uuid: context.team?.uuid === undefined ? null : context.team.uuid,
      team_uuid: propsTeamNoteTeamUUID,
      title: '[Daily]振り返り',
      goal_id: goal?.id === undefined ? null : goal.id,
      date: propsDateString,
      stats: null,
      gbn_log: {
        good: null,
        bad: null,
        next: null
      },
      scores: [
        // NOTE: スコアON時にデフォルトで１つ表示したいので初期値を設定（※新規登録時だけ）
        {
          team1: null,
          team2: null,
          team1_1st_half_score: null,
          team2_1st_half_score: null,
          team1_2nd_half_score: null,
          team2_2nd_half_score: null,
          team1_extra_1st_half_score: null,
          team2_extra_1st_half_score: null,
          team1_extra_2nd_half_score: null,
          team2_extra_2nd_half_score: null,
          team1_pk_score: null,
          team2_pk_score: null
        }
      ],
      memos: [{ memo: null }],
      scene: { tactical_board: undefined }
    }
  });

  /**
   * React Hook Form - のuseFieldArrayを使用して動的フォームを作成
   */
  const {
    fields: scoreFields,
    append: appendScore,
    remove: removeScore
  } = useFieldArray({
    control,
    name: 'scores'
  });

  const {
    fields: memoFields
    // append: appendMemo,
    // remove: removeMemo
  } = useFieldArray({
    control,
    name: 'memos'
  });

  /**
   * React Hook Form register
   * FIXME: 編集ボタン付きテキストエリアで、ref参照つきで制御する場合、
   * 下記のように実装しないと機能しない。他と実装が揃っていないので他に良い実装があればリファクタしたい
   *
   * 経緯：
   * Controlでラップして実装するパターンだと、フォース制御が外れてしまい、スマホブラウザの挙動がおかしくなる
   * なのでControlを使用しないパターンでuseRefつきのフォームを作成しようとしたら以下のような実装になった。
   * https://www.react-hook-form.com/faqs/#Howtosharerefusage
   */
  const { ref: refGood, ...restGood } = register('gbn_log.good');
  const { ref: refBad, ...restBad } = register('gbn_log.bad');
  const { ref: refNext, ...restNext } = register('gbn_log.next');

  // NOTE: 動的フォームMemosのregisterを作成するヘルパー関数
  const getRegisterProps = (index: number) => {
    const { ref: refMemo, ...restMemo } = register(`memos.${index}.memo` as const);
    return {
      ...restMemo,
      ref: (el: HTMLTextAreaElement | null) => {
        refMemo(el);
        textareaMemoRefs.current[index] = el;
      }
    };
  };

  /**
   * React Hook Form watch
   */
  const watchGood = watch('gbn_log.good', '');
  const watchBad = watch('gbn_log.bad', '');
  const watchNext = watch('gbn_log.next', '');
  const watchTeamUUID = watch('team_uuid', '');
  const watchMemos = watch('memos', []);

  /**
   * useEffect
   */
  // デフォルトフォーカス
  // NOTE: ドロワーのフラグを監視して、表示されたらフォーカス
  // useEffect(() => {
  //   setFocus('gbn_log.title')
  //   scrollToTop()
  // }, [isOpen])

  // ノートカスタムチェックボックスの状態監視
  useEffect(() => {
    setNoteCustomCheckStates(checkStates);
  }, [checkStates]);

  useEffect(() => {
    setValue('date', propsDateString);
  }, [propsDateString]);

  useEffect(() => {
    setGoal(currentGoalData?.data?.goal);
    setValue('goal_id', currentGoalData?.data?.goal?.id === undefined ? null : currentGoalData?.data?.goal?.id);
  }, [currentGoalData?.data?.goal]);

  const handleSetGoal = (selectedGoal: GoalModel | undefined) => {
    setGoal(selectedGoal);
    setValue('goal_id', selectedGoal?.id || null);
  };

  const handleSetTacticalBoardData = (value: string) => {
    setValue('scene.tactical_board', value);
  };

  const handleUnsetTacticalBoardData = () => {
    setValue('scene.tactical_board', undefined);
  };

  // NOTE: カリー化した関数を作成（indexは等モジュールで固定したものを渡したい）
  const handleInputTeam1 = (index: number) => (value: string) => {
    setScores((prevScores) => {
      // 不変性を保つために配列をコピー
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        // オブジェクトもコピーして更新
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team1: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team1`, value);
  };

  const handleInputTeam2 = (index: number) => (value: string) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team2: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team2`, value);
  };

  const handleChangeTeam1FirstHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team1FirstHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team1_1st_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam2FirstHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team2FirstHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team2_1st_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam1SecondHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team1SecondHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team1_2nd_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam2SecondHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team2SecondHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team2_2nd_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam1ExtraFirstHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team1ExtraFirstHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team1_extra_1st_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam2ExtraFirstHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team2ExtraFirstHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team2_extra_1st_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam1ExtraSecondHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team1ExtraSecondHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team1_extra_2nd_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam2ExtraSecondHalfScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team2ExtraSecondHalfScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team2_extra_2nd_half_score`, value === undefined ? null : value);
  };

  const handleChangeTeam1PKScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team1PKScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team1_pk_score`, value === undefined ? null : value);
  };

  const handleChangeTeam2PKScore = (index: number) => (value: number | undefined) => {
    setScores((prevScores) => {
      const updatedScores = [...prevScores];
      if (updatedScores[index]) {
        updatedScores[index] = {
          ...(updatedScores[index] as ScoreCardData),
          team2PKScore: value
        };
      }
      return updatedScores;
    });

    setValue(`scores.${index}.team2_pk_score`, value === undefined ? null : value);
  };

  const handleSelectStats = (value: number | undefined) => {
    setValue('stats', value === undefined ? null : value);
  };

  // const scrollToTop = () => {
  //   // ドロワーコンテンツのidを使用して要素を取得し、scrollTopを設定する
  //   const drawerContent = document.getElementById('drawer-content');
  //   if (drawerContent) {
  //     drawerContent.scrollTop = 0;
  //   }
  // };

  /**
   * 箇条書き挿入
   */
  const insertBulletPoint = (
    target_form_name: 'gbn_log.good' | 'gbn_log.bad' | 'gbn_log.next' | `memos.${number}.memo`,
    ref: React.RefObject<HTMLTextAreaElement>
  ) => {
    const textarea = ref.current;

    if (textarea) {
      let newValueWithBulletPoint = '';
      let space = 0;

      // カーソル位置取得
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // 現在のValue値を取得
      const value = textarea.value;

      // 行単位で文字列分割
      const lines = value.split('\n');
      // 現在のカーソル位置の行数
      const currentLineIndex = value.slice(0, start).split('\n').length - 1;
      // 現在のカーソル位置の行の文字列
      const currentLine = lines[currentLineIndex] || '';
      // カーソル位置が行頭か
      const isAtLineStart = start === 0 || value.charAt(start - 1) === '\n';
      // カーソル位置が行末か
      const isAtLineEnd = end === value.length || value.charAt(end) === '\n';
      // カーソル位置が行途中か
      const isInTheMiddleOfLine = !isAtLineStart && !isAtLineEnd;

      // console.log(lines)
      // console.log(`currentLineIndex: ${currentLineIndex}`)
      // console.log(`currentLine: ${currentLine}`)
      // console.log(`isAtLineStart: ${isAtLineStart}`)
      // console.log(`isAtLineEnd: ${isAtLineEnd}`)
      // console.log(`isInTheMiddleOfLine: ${isInTheMiddleOfLine}`)

      // 行頭
      if (isAtLineStart) {
        //カーソル以降文字列なし
        if (currentLine === '') {
          newValueWithBulletPoint = value.substring(0, start) + '• ' + value.substring(end);
          space = 2;
        } else {
          newValueWithBulletPoint = value.substring(0, start) + '• ' + '\n' + value.substring(end);
          space = 2;
        }
        // 行途中
      } else if (isInTheMiddleOfLine) {
        //カーソル以降文字列なし
        if (currentLine === '') {
          newValueWithBulletPoint = value.substring(0, start) + '• ' + value.substring(end);
          space = 2;
        } else {
          newValueWithBulletPoint = value.substring(0, start) + '\n' + '• ' + value.substring(end);
          space = 3;
        }
        // 行末
      } else if (isAtLineEnd) {
        newValueWithBulletPoint = value.substring(0, start) + '\n' + '• ' + value.substring(end);
        space = 3;
      }

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setValue(target_form_name, newValueWithBulletPoint);
      textarea.value = newValueWithBulletPoint;

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setFocus(target_form_name);
      // NOTE:ReactがDOMを更新した後にカーソル位置を設定する必要がある。
      // setTimeoutはタスクキューに入れられるため、Reactのレンダリングサイクルが完了した後に実行される。
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + space;
        textarea.focus();
      }, 0);
    }
  };

  /**
   * 右矢印挿入
   */
  const insertArrow = (
    target_form_name: 'gbn_log.good' | 'gbn_log.bad' | 'gbn_log.next' | `memos.${number}.memo`,
    ref: React.RefObject<HTMLTextAreaElement>
  ) => {
    const textarea = ref.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue = value.substring(0, start) + '→ ' + value.substring(end);

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setValue(target_form_name, newValue, { shouldValidate: true });
      textarea.value = newValue;

      // NOTE: 状態を更新した上でDOMの値を書き換えないと画面に反映されない
      setFocus(target_form_name);
      // NOTE:ReactがDOMを更新した後にカーソル位置を設定する必要がある。
      // setTimeoutはタスクキューに入れられるため、Reactのレンダリングサイクルが完了した後に実行される。
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
        textarea.focus();
      }, 0);
    }
  };

  /**
   * Goalsへ遷移（目標を設定してない場合）
   */
  const handleMoveToGoals = () => {
    closeConfirmModal();
    closeDrawer();
    navigate('/goals');
  };

  /**
   * Goalsへ遷移（目標を設定してない場合） 確認モーダル表示
   */
  const handleConfirmMoveToGoals = () => {
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={handleMoveToGoals}>
        My Goals設定メニューへ移動します。
        <br />
        ※入力内容は破棄されます。
        <br />
        よろしいですか？
      </ConfirmModalContent>
    );
  };

  /**
   * フォームサブミット
   */
  const handleFormSubmit = async (formData: FootballlogCreateDataModel) => {
    // NOTE: スコアボードがチェックされていない場合は、送信しない。
    // 初期値がそのまま送られてしまうのを防止する
    if (!noteCustomCheckStates.isCheckedScore) {
      formData.scores = [];
    }

    // memoを表示して空白登録した場合、memos:[{id: 0, memo: null}]をパラメタから削除
    formData.memos = formData.memos.filter((memo) => memo.memo !== undefined && memo.memo !== null);

    // メモがチェックされていない場合は、送信しない
    if (!noteCustomCheckStates.isCheckedMemo) {
      formData.memos = [];
    }

    // ボタンローディング表示
    setIsLoading(true);

    // console.log(formData);
    try {
      // GTM/GA イベント送信
      sendGTMEvent('createNote');

      // サブミット処理（親コンポーネントでデータ更新）
      await onFormSubmit(formData);
      // ボタンローディング非表示
      setIsLoading(false);
    } catch {
      // ボタンローディング非表示
      setIsLoading(false);
    }
  };

  /**
   * 確認モーダルOK時の処理
   */
  const handleConfirmCloseOK = () => {
    // 確認モーダル閉じる（このタイミングがUX的に重要）
    closeConfirmModal();
    closeDrawer();
  };

  /**
   * 確認モーダル表示
   */
  const handleConfirmClose = () => {
    openConfirmModal(
      <ConfirmModalContent onClickOKCallback={handleConfirmCloseOK}>
        新規作成を終了します。
        <br />
        よろしいですか？
      </ConfirmModalContent>
    );
  };

  // スコア要素追加
  // NOTE: スコアスライダーコンポーネントとreact-hook-form(スコア動的フォーム)の二つに対して
  // 操作を行い、値を同期させている
  const handleAddScore = () => {
    // スコアスライダーコンポーネント用の初期データを追加
    const newScore: ScoreCardData = {
      team1: undefined,
      team2: undefined,
      team1FirstHalfScore: undefined,
      team2FirstHalfScore: undefined,
      team1SecondHalfScore: undefined,
      team2SecondHalfScore: undefined,
      team1ExtraFirstHalfScore: undefined,
      team2ExtraFirstHalfScore: undefined,
      team1ExtraSecondHalfScore: undefined,
      team2ExtraSecondHalfScore: undefined,
      team1PKScore: undefined,
      team2PKScore: undefined
    };
    setScores([...scores, newScore]);

    // react-hook-form のScoreCreateDataModelを追加
    appendScore({
      team1: null,
      team2: null,
      team1_1st_half_score: null,
      team2_1st_half_score: null,
      team1_2nd_half_score: null,
      team2_2nd_half_score: null,
      team1_extra_1st_half_score: null,
      team2_extra_1st_half_score: null,
      team1_extra_2nd_half_score: null,
      team2_extra_2nd_half_score: null,
      team1_pk_score: null,
      team2_pk_score: null
    });
  };

  // スコア要素削除
  // NOTE: スコアスライダーコンポーネントとreact-hook-form(スコア動的フォーム)の二つに対して
  // 操作を行い、値を同期させている
  const handleRemoveScore = (index: number) => {
    // スコアコンポーネントから削除
    const newScores = scores.filter((_, i) => i !== index);
    setScores(newScores);

    // react-hook-form管理下のスコア要素を削除
    removeScore(index);
  };

  useEffect(() => {
    setValue('team_uuid', propsTeamNoteTeamUUID);
  }, [propsTeamNoteTeamUUID]);

  useEffect(() => {
    const user_team = context.user_teams?.find((ut) => ut.team.uuid === watchTeamUUID);
    setSelectedTeam(user_team?.team);
  }, [watchTeamUUID]);

  useEffect(() => {
    if (isErrorCurrentGoalData && !hasShownCurrentGoalErrorToast.current) {
      showErrorMessage('データ通信に失敗しました。しばらくしてから再度お試しください。');
      hasShownCurrentGoalErrorToast.current = true;
      setIsDisabled(true);
    }
  }, [isErrorCurrentGoalData]);

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Input id="team_uuid" type="hidden" {...register('team_uuid')} />
      <Input id="date" type="hidden" {...register('date')} />
      {errors.date && <FormErrorText>{errors.date.message}</FormErrorText>}
      <div id="drawer-content">
        <div className="pt-4 px-4 pb-8">
          {watchTeamUUID && seletctedTeam && (
            <div className="pb-4">
              <TeamLabel
                className="text-lg"
                propsTeamName={seletctedTeam.name}
                propsColorA={seletctedTeam.color_a}
                propsColorB={seletctedTeam.color_b}
                propsTeamEmblem={seletctedTeam.team_emblem?.file_path}
              />
            </div>
          )}
          <div className="pb-8">
            <FormLabel sub="（タイトル）">Title</FormLabel>
            <Input
              id="title"
              type="text"
              {...register('title')}
              className="text-2xl font-semibold w-full"
              placeholder="タイトル"
            />
            {errors.title && <FormErrorText>{errors.title.message}</FormErrorText>}
          </div>
          <div className="pb-8">
            <FormLabel sub="（今の目標・課題）">My Goals</FormLabel>
            <Input id="goal_id" type="hidden" {...register('goal_id')} />
            {isLoadingCurrentGoalData ? (
              <FootballlogGoalsEditLoading />
            ) : (
              <FootballlogGoalsEdit
                propsGoal={currentGoalData?.data?.goal}
                onChangeGoal={handleSetGoal}
                onMoveToGoals={handleConfirmMoveToGoals}
              />
            )}
          </div>
          <div className={`pb-8 ${noteCustomCheckStates.isCheckedScore ? 'block' : 'hidden'}`}>
            <FormLabel sub="（ゲームスコア）">Score</FormLabel>
            <ScoreCardSlider
              isEdit={true}
              propsScores={scores}
              onInputTeam1={handleInputTeam1}
              onInputTeam2={handleInputTeam2}
              onChangeTeam1FirstHalfScore={handleChangeTeam1FirstHalfScore}
              onChangeTeam2FirstHalfScore={handleChangeTeam2FirstHalfScore}
              onChangeTeam1SecondHalfScore={handleChangeTeam1SecondHalfScore}
              onChangeTeam2SecondHalfScore={handleChangeTeam2SecondHalfScore}
              onChangeTeam1ExtraFirstHalfScore={handleChangeTeam1ExtraFirstHalfScore}
              onChangeTeam2ExtraFirstHalfScore={handleChangeTeam2ExtraFirstHalfScore}
              onChangeTeam1ExtraSecondHalfScore={handleChangeTeam1ExtraSecondHalfScore}
              onChangeTeam2ExtraSecondHalfScore={handleChangeTeam2ExtraSecondHalfScore}
              onChangeTeam1PKScore={handleChangeTeam1PKScore}
              onChangeTeam2PKScore={handleChangeTeam2PKScore}
              onAddScore={handleAddScore}
              onRemoveScore={handleRemoveScore}
            />
            {scoreFields.map((field, index) => (
              <div key={field.id}>
                <Input id={`scores.${index}.team1`} type="hidden" {...register(`scores.${index}.team1`)} />
                <Input id={`scores.${index}.team2`} type="hidden" {...register(`scores.${index}.team2`)} />
                <Input
                  id={`scores.${index}.team1_1st_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team1_1st_half_score`)}
                />
                <Input
                  id={`scores.${index}.team2_1st_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team2_1st_half_score`)}
                />
                <Input
                  id={`scores.${index}.team1_2nd_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team1_2nd_half_score`)}
                />
                <Input
                  id={`scores.${index}.team2_2nd_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team2_2nd_half_score`)}
                />
                <Input
                  id={`scores.${index}.team1_extra_1st_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team1_extra_1st_half_score`)}
                />
                <Input
                  id={`scores.${index}.team2_extra_1st_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team2_extra_1st_half_score`)}
                />
                <Input
                  id={`scores.${index}.team1_extra_2nd_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team1_extra_2nd_half_score`)}
                />
                <Input
                  id={`scores.${index}.team2_extra_2nd_half_score`}
                  type="hidden"
                  {...register(`scores.${index}.team2_extra_2nd_half_score`)}
                />
                <Input
                  id={`scores.${index}.team1_pk_score`}
                  type="hidden"
                  {...register(`scores.${index}.team1_pk_score`)}
                />
                <Input
                  id={`scores.${index}.team2_pk_score`}
                  type="hidden"
                  {...register(`scores.${index}.team2_pk_score`)}
                />
                {errors.scores?.[index]?.team1 && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team1?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team2 && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team2?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team1_1st_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team1_1st_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team2_1st_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team2_1st_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team1_2nd_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team1_2nd_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team2_2nd_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team2_2nd_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team1_extra_1st_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team1_extra_1st_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team2_extra_1st_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team2_extra_1st_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team1_extra_2nd_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team1_extra_2nd_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team2_extra_2nd_half_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team2_extra_2nd_half_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team1_pk_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team1_pk_score?.message}</FormErrorText>
                  </div>
                )}
                {errors.scores?.[index]?.team2_pk_score && (
                  <div>
                    <FormErrorText>{errors.scores?.[index]?.team2_pk_score?.message}</FormErrorText>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="pb-8">
            <FormLabel sub="（良かった点・反省点・次に向けて）">Good・Bad・Next</FormLabel>
            <div className="border border-gray-300 focus-within:ring-blue-500 focus-within:ring-1 focus-within:border-blue-500 rounded-lg bg-gray-50 p-1">
              <div className="flex justify-between items-center bg-gray-100 rounded shadow mb-1 p-2">
                <div className="w-40">
                  <PolygonDivider color="green" withBorder={false}>
                    Good
                    <svg
                      className="inline w-2.5 h-2.5 ms-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 18 18">
                      <path d="M3 7H1a1 1 0 0 0-1 1v8a2 2 0 0 0 4 0V8a1 1 0 0 0-1-1Zm12.954 0H12l1.558-4.5a1.778 1.778 0 0 0-3.331-1.06A24.859 24.859 0 0 1 6 6.8v9.586h.114C8.223 16.969 11.015 18 13.6 18c1.4 0 1.592-.526 1.88-1.317l2.354-7A2 2 0 0 0 15.954 7Z" />
                    </svg>
                  </PolygonDivider>
                </div>
                <EditorShortcuts
                  onInsertArrow={() => insertArrow('gbn_log.good', textareaGoodRef)}
                  onInsertBulletPoint={() => insertBulletPoint('gbn_log.good', textareaGoodRef)}
                />
              </div>
              <Textarea
                id="good"
                rows={5}
                className="text-base w-full border-0 focus:ring-0"
                placeholder="良かった点、成功したプレーなど"
                {...restGood}
                ref={(e) => {
                  refGood(e);
                  textareaGoodRef.current = e;
                }}>
                {MAX_LENGTH_GOOD - (watchGood?.length || 0) <= 20 && (
                  <div
                    className={`text-xs font-semibold ${MAX_LENGTH_GOOD - (watchGood?.length || 0) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                    残り{MAX_LENGTH_GOOD - (watchGood?.length || 0)}文字
                  </div>
                )}
              </Textarea>
              <div className="flex justify-between items-center bg-gray-100 rounded shadow mb-1 p-2">
                <div className="w-40">
                  <PolygonDivider color="indigo" withBorder={false}>
                    Bad
                    <svg
                      className="inline w-2.5 h-2.5 ms-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 18 18">
                      <path d="M11.955 2.117h-.114C9.732 1.535 6.941.5 4.356.5c-1.4 0-1.592.526-1.879 1.316l-2.355 7A2 2 0 0 0 2 11.5h3.956L4.4 16a1.779 1.779 0 0 0 3.332 1.061 24.8 24.8 0 0 1 4.226-5.36l-.003-9.584ZM15 11h2a1 1 0 0 0 1-1V2a2 2 0 1 0-4 0v8a1 1 0 0 0 1 1Z" />
                    </svg>
                  </PolygonDivider>
                </div>
                <EditorShortcuts
                  onInsertArrow={() => insertArrow('gbn_log.bad', textareaBadRef)}
                  onInsertBulletPoint={() => insertBulletPoint('gbn_log.bad', textareaBadRef)}
                />
              </div>
              <Textarea
                id="bad"
                rows={5}
                className="text-base w-full border-0 focus:ring-0"
                placeholder="反省点、失敗したプレーなど"
                {...restBad}
                ref={(e) => {
                  refBad(e);
                  textareaBadRef.current = e;
                }}>
                {MAX_LENGTH_BAD - (watchBad?.length || 0) <= 20 && (
                  <div
                    className={`text-xs font-semibold ${MAX_LENGTH_BAD - (watchBad?.length || 0) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                    残り{MAX_LENGTH_BAD - (watchBad?.length || 0)}文字
                  </div>
                )}
              </Textarea>
              <div className="flex justify-between items-center bg-gray-100 rounded shadow mb-1 p-2">
                <div className="w-40">
                  <PolygonDivider color="yellow" withBorder={false}>
                    Next
                    <svg
                      className="inline w-3 h-3 ms-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 18 16">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 10l3-4 4 4 5-5m0 0h-3.207M16 5v3.207"
                      />
                    </svg>
                  </PolygonDivider>
                </div>
                <EditorShortcuts
                  onInsertArrow={() => insertArrow('gbn_log.next', textareaNextRef)}
                  onInsertBulletPoint={() => insertBulletPoint('gbn_log.next', textareaNextRef)}
                />
              </div>
              <Textarea
                id="next"
                rows={5}
                className="text-base w-full border-0 focus:ring-0"
                placeholder="次にむけて改善すること、意識すること"
                {...restNext}
                ref={(e) => {
                  refNext(e);
                  textareaNextRef.current = e;
                }}>
                {MAX_LENGTH_NEXT - (watchNext?.length || 0) <= 20 && (
                  <div
                    className={`text-xs font-semibold ${MAX_LENGTH_NEXT - (watchNext?.length || 0) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                    残り{MAX_LENGTH_NEXT - (watchNext?.length || 0)}文字
                  </div>
                )}
              </Textarea>
            </div>
            {errors.gbn_log && errors.gbn_log.good && (
              <div>
                <FormErrorText>{errors.gbn_log.good.message}</FormErrorText>
              </div>
            )}
            {errors.gbn_log && errors.gbn_log.bad && (
              <div>
                <FormErrorText>{errors.gbn_log.bad.message}</FormErrorText>
              </div>
            )}
            {errors.gbn_log && errors.gbn_log.next && (
              <div>
                <FormErrorText>{errors.gbn_log.next.message}</FormErrorText>
              </div>
            )}
          </div>
          <div className={`pb-8 ${noteCustomCheckStates.isCheckedMemo ? 'block' : 'hidden'}`}>
            <div className="flex items-center">
              <FormLabel sub="（自由記入）">Memo</FormLabel>
              <PrivateAicon />
            </div>
            {memoFields.map((field, index) => (
              <div key={field.id}>
                <div className="border border-gray-300 focus-within:ring-blue-500 focus-within:ring-1 focus-within:border-blue-500 rounded-lg bg-gray-50 p-1">
                  <div className="flex justify-between items-center bg-gray-100 rounded p-2 shadow mb-1">
                    <div></div>
                    <EditorShortcuts
                      onInsertArrow={() => {
                        const singleRef: React.RefObject<HTMLTextAreaElement> = {
                          current: textareaMemoRefs.current[index] || null // 該当する textarea 要素
                        };
                        insertArrow(`memos.${index}.memo`, singleRef);
                      }}
                      onInsertBulletPoint={() => {
                        const singleRef: React.RefObject<HTMLTextAreaElement> = {
                          current: textareaMemoRefs.current[index] || null // 該当する textarea 要素
                        };
                        insertBulletPoint(`memos.${index}.memo`, singleRef);
                      }}
                    />
                  </div>
                  <Textarea
                    id="memo"
                    rows={5}
                    className="text-base w-full border-0 focus:ring-0"
                    placeholder="練習内容やコーチからのアドバイス、嬉しかった・悔しかったその時の感情など、自由に書こう"
                    {...getRegisterProps(index)}>
                    {MAX_LENGTH_MEMO - (watchMemos?.[index]?.memo?.length || 0) <= 20 && (
                      <div
                        className={`text-xs font-semibold ${MAX_LENGTH_MEMO - (watchMemos?.[index]?.memo?.length || 0) < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        残り{MAX_LENGTH_MEMO - (watchMemos?.[index]?.memo?.length || 0)}文字
                      </div>
                    )}
                  </Textarea>
                </div>
                {errors.memos?.[index]?.memo && (
                  <div>
                    <FormErrorText>{errors.memos?.[index]?.memo?.message}</FormErrorText>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="pb-8">
            <FormLabel sub="（印象に残っているプレーシーン）">Scene</FormLabel>
            <SelectTacticalboard
              onSelectTacticalBoard={handleSetTacticalBoardData}
              onUnselectTacticalBoard={handleUnsetTacticalBoardData}
            />
            <Input id="scene" type="hidden" {...register('scene.tactical_board')} />
            {errors.scene && errors.scene.tactical_board && (
              <FormErrorText>{errors.scene.tactical_board.message}</FormErrorText>
            )}
          </div>
          <div className="pb-8">
            <FormLabel sub="（目標・課題に対する評価、総評）">Stats</FormLabel>
            <SelectStats stats={0} onSelectStats={handleSelectStats} />
            <Input id="stats" type="hidden" {...register('stats')} />
            {errors.stats && <FormErrorText>{errors.stats.message}</FormErrorText>}
          </div>
        </div>
        <div
          className="flex items-center justify-end border-t border-green-500 pb-safe bg-white sticky bottom-0 z-20"
          style={context.team ? { borderTop: `solid 1px ${context.team.color_a}` } : {}}>
          <Button
            className="mt-4 mb-4 me-2"
            type="button"
            buttonType={context.team ? 'outlineSecondary' : 'outlinePrimary'}
            onClick={handleConfirmClose}>
            キャンセル
          </Button>
          <Button
            type="submit"
            className="mt-4 mb-4 me-4"
            buttonType={context.team ? 'outlineSecondary' : 'primary'}
            isLoading={isLoading}
            disabled={isDisabled}>
            OK
          </Button>
        </div>
      </div>
    </form>
  );
};

export default FootballlogForm;
